import React from 'react';
import { Button } from '@getro/rombo';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';

import { trackEvent } from '../../../helpers/analytics';
import { useShareContactsMutation } from '../../../services/userV2';

export const ShareContacts = ({ handleNextStep, activeNetwork }) => {
  const [shareContacts, { isLoading: isLoadingShareContacts }] = useShareContactsMutation();
  const items = [
    {
      id: 'browse-contacts',
      title: `${activeNetwork?.name} can browse your contacts`,
      description: 'You can always change who can see your contacts.',
    },
    {
      id: 'ask-for-intros',
      title: `${activeNetwork?.name} ask you for intros`,
      description: `The ${activeNetwork?.name} team will ask you for a warm intro when they see a good fit.`,
    },
    {
      id: 'make-intro',
      title: 'You make the intro',
      description: `You choose which intros to make, helping both ${activeNetwork?.name} and your contacts`,
    },
  ];

  const onClickShareContacts = () => {
    shareContacts({
      collectionId: activeNetwork?.id,
      share: true,
    })
      .unwrap()
      .then(() => {
        trackEvent('contacts:contacts_sharing:enabled', {
          collectionId: activeNetwork?.id,
          page: 'onboarding',
        });
        handleNextStep();
      });
  };

  return (
    <Flex
      sx={{
        mx: 'auto',
        mt: '100px',
        pb: '40px',
        px: '16px',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        maxWidth: '432px',
      }}
    >
      <Flex sx={{ flexDirection: 'column', gap: '24px' }}>
        <Text as="h1" fontSize="22px" fontWeight="600" lineHeight="1.2">
          Would you like to share your contacts with {activeNetwork.name}?
        </Text>
        <Text color="text.main" fontSize="16px" lineHeight="1.4">
          Make it easy to help {activeNetwork.name}:
        </Text>
      </Flex>
      <Flex sx={{ gap: '24px', width: '100%', flexDirection: 'column' }}>
        <Flex sx={{ flexDirection: 'column', gap: '16px', width: '100%' }}>
          {items.map((item, index) => (
            <Flex key={item.id} alignItems="flex-start" sx={{ gap: '8px' }}>
              <Flex
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                sx={{ width: '24px', height: '24px', borderRadius: 'circle', bg: 'purple.100' }}
              >
                <Text fontSize="14px" lineHeight="1.4" fontWeight="500" color="purple.500">
                  {index + 1}
                </Text>
              </Flex>
              <Box>
                <Text color="text.dark" fontSize="14px" lineHeight="1.4" fontWeight="500">
                  {item.title}
                </Text>
                <Text mt="4px" color="text.main" fontSize="14px" lineHeight="1.4" fontWeight="400">
                  {item.description}
                </Text>
              </Box>
            </Flex>
          ))}
        </Flex>
        <Flex alignItems="center" justifyContent="center" sx={{ gap: '16px' }}>
          <Button flex="3" variant="secondary" onClick={handleNextStep} sx={{ justifyContent: 'center' }}>
            Not for now
          </Button>
          <Button
            flex="4"
            loading={isLoadingShareContacts}
            onClick={onClickShareContacts}
            sx={{ justifyContent: 'center' }}
          >
            Yes, share my contacts
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

ShareContacts.propTypes = {
  handleNextStep: PropTypes.func.isRequired,
  activeNetwork: PropTypes.object.isRequired,
};
